import apiClient from '@app/auth/auth-api-client'
import { iframeAuthProxy } from './frame-proxy'

const userBackendApi = apiClient(IDENTITY_SERVICE_URL)
const lenderBackendApi = apiClient(API2_URL)

/**
 * Unwrap a proxy response, throwing on failure to match axios behavior.
 * @param {Object} result - The proxy response { success, status, statusText, data, error }
 * @returns {*} result.data on success
 * @throws {Error} on failure
 */
function unwrapProxyResponse(result) {
  if (!result.success) {
    const message = result.error || result.data?.detail || result.data?.error || `Proxy request failed with status ${result.status}`
    const error = new Error(message)
    error.status = result.status
    error.data = result.data
    throw error
  }
  return result.data
}

/**
 * Used when the auth config request fails
 */
export const FALLBACK_AUTH_CONFIG = {
  use_frame: true,
}

export const userServiceEndpoints = {
  /**
   * @param {string} username - The username (email) for login
   * @param {string} password - The password for login
   * @param {string} lender_id - The lender account id for login
   * @param {Object} [options] - Optional settings
   * @param {boolean} [options.useProxy=false] - Whether to use the iframe proxy
   * @returns {Promise<{access_token: string, expires: number}>} New bearer token with expiration
   */
  LOGIN: async (username, password, lender_id, { useProxy = false } = {}) => {
    if (!lender_id) {
      throw new Error('Account selection required')
    }
    if (!username || !password) {
      throw new Error('Username and password are required')
    }

    if (useProxy) {
      const result = await iframeAuthProxy.operation('auth:login', {
        body: { username, password, lender_id, client_id: IDENTITY_CLIENT_ID },
      })
      return unwrapProxyResponse(result)
    }

    const formData = new FormData()
    formData.append('username', username)
    formData.append('password', password)
    formData.append('lender_id', lender_id)
    formData.append('client_id', IDENTITY_CLIENT_ID)
    const loginResult = await userBackendApi.postForm('/lender/auth/login', {}, formData, { credentials: 'include' })
    return loginResult
  },
  /**
   * @param {string|undefined} loginCode - The login code to establish a session
   * @param {Object} [options] - Optional settings
   * @param {boolean} [options.useProxy=false] - Whether to use the iframe proxy
   * @returns {Promise<{access_token: string, expires: number}>} New bearer token with expiration
   */
  LOGIN_CODE: async (loginCode, { useProxy = false } = {}) => {
    if (!loginCode) {
      throw new Error('Login code is required')
    }

    if (useProxy) {
      const result = await iframeAuthProxy.operation('auth:login-code', {
        body: { code: loginCode, client_id: IDENTITY_CLIENT_ID },
      })
      return unwrapProxyResponse(result)
    }

    const formData = new FormData()
    formData.append('code', loginCode)
    formData.append('client_id', IDENTITY_CLIENT_ID)
    const codeResult = await userBackendApi.postForm('/lender/auth/login/code', {}, formData, { credentials: 'include' })
    return codeResult
  },
  /**
   * @param {string|undefined} loginToken - The bearer token for login
   * @param {Object} [options] - Optional settings
   * @param {boolean} [options.useProxy=false] - Whether to use the iframe proxy
   * @returns {Promise<{access_token: string, expires: number}>} The existing bearer token with expiration
   */
  LOGIN_TOKEN: async (loginToken, { useProxy = false } = {}) => {
    if (!loginToken) {
      throw new Error('Login token is required')
    }

    if (useProxy) {
      const result = await iframeAuthProxy.operation('auth:login-token', {
        body: { token: loginToken, client_id: IDENTITY_CLIENT_ID },
      })
      return unwrapProxyResponse(result)
    }

    const formData = new FormData()
    formData.append('token', loginToken)
    formData.append('client_id', IDENTITY_CLIENT_ID)
    const tokenResult = await userBackendApi.postForm('/lender/auth/login/token', {}, formData, { credentials: 'include' })
    return tokenResult
  },
  /**
   * @param {string|undefined} currentBearerToken - The current bearer token for authorization
   * @param {Object} [options] - Optional settings
   * @param {boolean} [options.useProxy=false] - Whether to use the iframe proxy
   * @returns {Promise<null>} Returns nothing
   */
  LOGOUT: async (currentBearerToken, { useProxy = false } = {}) => {
    const headers = {}
    if (currentBearerToken) {
      headers.Authorization = `Bearer ${currentBearerToken}`
    }

    if (useProxy) {
      const result = await iframeAuthProxy.operation('auth:logout', { headers })
      unwrapProxyResponse(result)
      return
    }

    await userBackendApi.post('/lender/auth/logout', {}, {}, { headers, credentials: 'include' })
  },
  /**
   * Get the authentication status and token information
   * @param {string|undefined} currentBearerToken - The current bearer token for authorization
   * @param {Object} [options] - Optional settings
   * @param {boolean} [options.useProxy=false] - Whether to use the iframe proxy
   * @returns {Promise<{access_token: string, expires: number}>} A valid bearer token with expiration
   */
  STATUS: async (currentBearerToken, { useProxy = false } = {}) => {
    const headers = {}
    if (currentBearerToken) {
      headers.Authorization = `Bearer ${currentBearerToken}`
    }

    if (useProxy) {
      const result = await iframeAuthProxy.operation('auth:status', { headers })
      return unwrapProxyResponse(result)
    }

    const tokenResult = await userBackendApi.get('/lender/auth/status', {}, { headers, credentials: 'include' })
    return tokenResult
  },
  /**
   * Get new bearer token for session
   * @param {Object} [options] - Optional settings
   * @param {boolean} [options.useProxy=false] - Whether to use the iframe proxy
   * @returns {Promise<{access_token: string, expires: number}>} The new bearer token with expiration
   */
  TOKEN: async ({ useProxy = false } = {}) => {
    if (useProxy) {
      const result = await iframeAuthProxy.operation('auth:token')
      return unwrapProxyResponse(result)
    }

    const tokenResult = await userBackendApi.post('/lender/auth/token', {}, {}, { credentials: 'include' })
    return tokenResult
  },
  /**
   * Get accounts associated with email
   * @param {string} email - The email to look up accounts for
   * @returns {Promise<{id: string, name: string, logo_url: string}[]>} The account options available for login
   */
  ACCOUNT_OPTIONS: async (email) => {
    const lenderAccounts = await userBackendApi.post('/lender/accounts', {}, { email: email })
    return lenderAccounts
  },
  /**
   * Get login methods for account
   * @param {string} accountId - The account id to look up login methods for
   * @returns {Promise<{type: string, url?: string}[]>} The login methods available for the account
   */
  ACCOUNT_LOGIN_METHODS: async (accountId) => {
    const lenderLoginMethods = await userBackendApi.post(`/lender/accounts/${accountId}/login_methods`, {}, {})
    return lenderLoginMethods
  },
}

export const lenderBackendEndpoints = {
  LOGIN: async (username, password, lender_id) => {
    if (!lender_id) {
      throw new Error('Account selection required')
    }
    if (!username || !password) {
      throw new Error('Username and password are required')
    }

    const queryParams = { lender: lender_id }
    const result = await lenderBackendApi.post('/auth', queryParams, { username, password })
    return result
  },
  LOGOUT: async (currentBearerToken) => {
    const headers = {}
    if (currentBearerToken) {
      headers.Authorization = `Bearer ${currentBearerToken}`
    }
    await lenderBackendApi.post('/auth/logout', {}, {}, { headers })
  },
  CURRENT_USER: async (currentBearerToken) => {
    const headers = {}
    if (currentBearerToken) {
      headers.Authorization = `Bearer ${currentBearerToken}`
    }
    const result = await lenderBackendApi.get('/users/current_user', {}, { headers })
    return result
  },
}

export const authConfigEndpoints = {
  /**
   * Get the authentication config
   * @returns {Promise<{use_frame: boolean}>} The authentication status with access token and expiration
   */
  GET_CONFIG: async () => {
    try {
      const result = await userBackendApi.get('/lender/auth/config', {}, { signal: AbortSignal.timeout(2500) })
      return result
    } catch (error) {
      // auth-api-client already logs timeouts and network errors at warn level,
      // so just return the fallback here
      return FALLBACK_AUTH_CONFIG
    }
  },
}
